/**
 * Shared employee compensation types for API contracts between frontend and backend
 * EmployeeCompensation tracks individual employee pay rates with a state machine lifecycle
 */
import { z } from "zod";
import { uuidSchema } from "./validation-common.js";
import { CompensationTypeSchema } from "./payConfiguration.js";
// ===== STATUS ENUM =====
export const EmployeeCompensationStatusSchema = z.enum(["PENDING", "ACTIVE", "INACTIVE"]);
// ===== CREATE INPUT SCHEMA =====
/**
 * Schema for creating an employee compensation record
 * Used when setting up compensation for an employee in a specific job role family
 */
export const CreateEmployeeCompensationInputSchema = z.object({
    employmentId: uuidSchema,
    jobRoleFamilyId: uuidSchema,
    compensationType: CompensationTypeSchema,
    payRate: z.number().positive(),
    tipEligible: z.boolean(),
    commissionEligible: z.boolean(),
    bonusEligible: z.boolean(),
    effectiveDate: z.string().datetime().optional(), // ISO datetime, defaults to now if not provided
    notes: z.string().max(2000).optional(),
});
// ===== UPDATE INPUT SCHEMA =====
/**
 * Schema for updating an employee compensation record (partial update)
 */
export const UpdateEmployeeCompensationInputSchema = z.object({
    compensationType: CompensationTypeSchema.optional(),
    payRate: z.number().positive().optional(),
    tipEligible: z.boolean().optional(),
    commissionEligible: z.boolean().optional(),
    bonusEligible: z.boolean().optional(),
    effectiveDate: z.string().datetime().optional(),
    notes: z.string().max(2000).optional(),
});
// ===== RESPONSE SCHEMA =====
/**
 * Zod schema matching the EmployeeCompensationResponse interface
 */
export const EmployeeCompensationResponseSchema = z.object({
    id: z.string(),
    employmentId: z.string(),
    jobRoleFamilyId: z.string(),
    compensationType: z.string(),
    payRate: z.number(),
    effectiveDate: z.string(),
    status: EmployeeCompensationStatusSchema,
    tipEligible: z.boolean(),
    commissionEligible: z.boolean(),
    bonusEligible: z.boolean(),
    authoredBy: z.string().nullable(),
    notes: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ===== STATE MACHINE =====
/**
 * Valid status transitions for EmployeeCompensation state machine
 * PENDING -> ACTIVE (when effectiveDate reached)
 * PENDING -> INACTIVE (cancelled before becoming active)
 * ACTIVE -> INACTIVE (replaced by new config or deactivated)
 * INACTIVE is terminal - no transitions out
 */
export const VALID_STATUS_TRANSITIONS = {
    PENDING: ["ACTIVE", "INACTIVE"],
    ACTIVE: ["INACTIVE"],
    INACTIVE: [],
};
